import React, { useRef, useEffect, useState } from 'react';
import { useHistory, useLocation } from "react-router-dom";
import MediaSwitcher from './MediaSwitcher';
import TextToHTML from './TextToHtml';
import TagList from './TagList';
import { PiCoins } from 'react-icons/pi';
import { IoMdImages } from 'react-icons/io';
import { LuFileVideo2 } from 'react-icons/lu';
import { TbShoppingBagPlus } from "react-icons/tb";

let currentPlayingVideo = null;

function CardPack(props) {
    const mediaSwitcherRef = useRef(null);
    const history = useHistory();
    const location = useLocation();

    const pauseVideo = () => {
        if (mediaSwitcherRef.current) {
            const videoElement = mediaSwitcherRef.current.querySelector('video');
            if (videoElement && !videoElement.paused) {
                videoElement.pause();
            }
        }
    };

    const handleVideoPlay = () => {
        if (mediaSwitcherRef.current) {
            const videoElement = mediaSwitcherRef.current.querySelector('video');
            if (videoElement) {
                if (currentPlayingVideo && currentPlayingVideo !== videoElement) {
                    currentPlayingVideo.pause();
                }
                currentPlayingVideo = videoElement;
            }
        }
    };

    const handleVideoClick = (event) => {
        event.stopPropagation();
    };

    useEffect(() => {
        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    pauseVideo();
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, {
            root: null,
            threshold: 0.1
        });

        if (mediaSwitcherRef.current) {
            observer.observe(mediaSwitcherRef.current);
            const videoElement = mediaSwitcherRef.current.querySelector('video');
            if (videoElement) {
                videoElement.addEventListener('play', handleVideoPlay);
                videoElement.addEventListener('click', handleVideoClick);
            }
        }

        return () => {
            if (mediaSwitcherRef.current) {
                observer.unobserve(mediaSwitcherRef.current);
                const videoElement = mediaSwitcherRef.current.querySelector('video');
                if (videoElement) {
                    videoElement.removeEventListener('play', handleVideoPlay);
                    videoElement.removeEventListener('click', handleVideoClick);
                }
            }
        };
    }, []);

    const handleMediaClick = () => {
        props.onMediaClick(props.pack.pack_id);
    };

    const [darkMode] = useState(() => {
        const savedMode = localStorage.getItem('darkMode');
        return savedMode !== null ? JSON.parse(savedMode) : false;
    });

    useEffect(() => {
        document.documentElement.classList.toggle('dark', darkMode);
    }, [darkMode]);

    const onAvatarClick = (userId) => {
        history.push(`/profileModal/${userId}`);
    };

    const isInProfileModal = location.pathname.includes("profileModal");

    return (
        // Vive dentro del modal del perfil (gray-800 en oscuro): la tarjeta va un
        // tono más clara (gray-700) y con color de texto propio; antes era
        // gray-900 sin color de texto y en oscuro quedaba negro sobre negro.
        <div className="overflow-hidden w-full max-w-[420px] h-auto md:w-[300px] md:h-[650px] md:max-w-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-[24px] ring-1 ring-black/5 dark:ring-white/[0.06] mx-auto md:mx-0 flex flex-col"
            style={{ fontFamily: 'var(--ap-font)', boxShadow: '0 1px 2px rgba(0,0,0,.06), 0 12px 32px rgba(0,0,0,.12)' }}>
            
            {/* MediaSwitcher */}
            <div className="relative flex-shrink-0">
                <MediaSwitcher 
                    files={props.pack.files.map(file => file.preview_file || file.processed_file)} 
                    className="p-0" 
                    ref={mediaSwitcherRef}
                />
                <div
                    className="absolute inset-0 bg-transparent z-10 cursor-pointer"
                    onClick={() => handleMediaClick()}
                ></div>
            </div>
    
            {/* Contenido principal */}
            <div className="px-4 pt-4 flex-1 flex flex-col">
                <div className="flex-1">
                    {/* Sección Superior */}
                    <div className=" mb-4 h-[50px]">
                        {/* Descripción */}
                        <div className="flex-1 bg-gray-100 dark:bg-gray-600/60 text-gray-800 dark:text-gray-100 p-3 rounded-[12px] overflow-auto h-[80px] text-[13px] leading-5">
                            <TextToHTML text={props.pack.description} />
                        </div>
    
                        {/* Avatar */}
                        {!isInProfileModal && (
                            <div className="flex-shrink-0">
                                <img
                                    src={props.pack.avatar_thumb_url || props.pack.model_profile_cover}
                                    alt="Profile"
                                    className="w-20 h-20 rounded-full object-cover border-2 border-white hover:border-rose-500 transition-all"
                                    onClick={() => onAvatarClick(props.pack.user_id)}
                                />
                            </div>
                        )}
                    </div>
    
                    {/* Estadísticas */}
                    <div className="grid grid-cols-3 pt-6 text-[13px] font-medium text-gray-900 dark:text-gray-100">
                        <div className="flex items-center justify-center">
                            <PiCoins className="text-rose-500 mr-1.5 text-lg" />
                            <span>{props.pack.tokens || 'Free'}</span>
                        </div>
                        <div className="flex items-center justify-center">
                            <IoMdImages className="text-gray-500 dark:text-gray-300 mr-1.5 text-lg" />
                            <span>{props.pack.num_images}</span>
                        </div>
                        <div className="flex items-center justify-center">
                            <LuFileVideo2 className="text-gray-500 dark:text-gray-300 mr-1.5 text-lg" />
                            <span>{props.pack.num_videos}</span>
                        </div>
                    </div>
    
                    {/* Tags */}
                    <div className="h-[60px] overflow-auto">
                        <TagList tags={props.pack.tags.split(',').map(String)} />
                    </div>
                </div>
    
                {/* Botón pegado al fondo */}
                <div className=" pt-4 mb-4">
                    <button
                        onClick={() => props.onClick(props.pack)}
                        className="ap-btn ap-btn-filled w-full">
                        <TbShoppingBagPlus className="text-xl" />
                        <span>Buy Pack</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CardPack;